import React, { useEffect, useState } from "react"
import EditAppointmentModal from "./EditAppointmentModal"

const TAB_OVERVIEW = "OVERVIEW"
const TAB_CLINICAL = "CLINICAL"
const TAB_BILLING = "BILLING"
const TAB_LEDGER = "LEDGER"

const VISUAL_TAG_COLORS = [
  "#EF4444",
  "#F59E0B",
  "#10B981",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#64748B",
]

const FDI_TOOTH_GROUPS = [
  {
    label: "Superior derecha",
    items: [18, 17, 16, 15, 14, 13, 12, 11],
  },
  {
    label: "Superior izquierda",
    items: [21, 22, 23, 24, 25, 26, 27, 28],
  },
  {
    label: "Inferior izquierda",
    items: [31, 32, 33, 34, 35, 36, 37, 38],
  },
  {
    label: "Inferior derecha",
    items: [48, 47, 46, 45, 44, 43, 42, 41],
  },
  {
    label: "Temporales superior derecha",
    items: [55, 54, 53, 52, 51],
  },
  {
    label: "Temporales superior izquierda",
    items: [61, 62, 63, 64, 65],
  },
  {
    label: "Temporales inferior izquierda",
    items: [71, 72, 73, 74, 75],
  },
  {
    label: "Temporales inferior derecha",
    items: [85, 84, 83, 82, 81],
  },
]

const paymentMethodOptions = [
  { value: "CASH", label: "Efectivo" },
  { value: "CARD", label: "Tarjeta" },
  { value: "TRANSFER", label: "Transferencia" },
  { value: "MIXED", label: "Mixto" },
]

export default function AppointmentDetailDrawer({
  appointment,
  onClose,
  patientSnapshot,
  financialSummary,
  loadingFinancialSummary,
  checkingIn,
  registeringTreatment,
  registeringPayment,
  creatingReceivable,
  updatingAppointment,
  treatments,
  doctors,
  chairs,
  selectedAppointmentTreatmentId,
  setSelectedAppointmentTreatmentId,
  treatmentOdontogramForm,
  setTreatmentOdontogramForm,
  surfaces,
  conditions,
  odontoStatuses,
  paymentForm,
  setPaymentForm,
  receivableForm,
  setReceivableForm,
  formatDateTime,
  formatMoney,
  statusLabel,
  isReadOnly,
  readOnlyReason,
  onOpenPatientWorkspace,
  onOpenTreatmentPlanModal,
  onCheckIn,
  onRegisterTreatment,
  onDeleteTreatment,
  onRegisterPayment,
  onCancelPayment,
  onCreateReceivable,
  onUpdateAppointmentColor,
  onUpdateAppointment,
  onCancelAppointment,
  onDeleteAppointment,
  onOpenPatientPage,
}) {
  const appointmentId = appointment?.id || ""
  const statusRaw = getStatusRaw(appointment)
  const statusDisplay = getStatusDisplayLabel(appointment, statusLabel)
  const [activeTab, setActiveTab] = useState(getDefaultTabByStatus(statusRaw))
  const [autoTabAppointmentId, setAutoTabAppointmentId] = useState("")
  const [editModalOpen, setEditModalOpen] = useState(false)

  useEffect(() => {
    setAutoTabAppointmentId("")
    setEditModalOpen(false)
  }, [appointmentId])

  useEffect(() => {
    if (!selectedAppointmentTreatmentId) return
    if (typeof setTreatmentOdontogramForm !== "function") return

    const treatment = Array.isArray(treatments)
      ? treatments.find((item) => String(item.id) === String(selectedAppointmentTreatmentId))
      : null

    const catalogPrice = getTreatmentCatalogPrice(treatment)

    setTreatmentOdontogramForm((prev) => {
      const currentPriceTreatmentId = prev?.priceTreatmentId || ""
      const currentPrice = prev?.price
      const hasManualPrice =
        currentPrice !== undefined &&
        currentPrice !== null &&
        String(currentPrice).trim() !== ""

      if (hasManualPrice && currentPriceTreatmentId === selectedAppointmentTreatmentId) {
        return prev
      }

      return {
        ...prev,
        price: String(catalogPrice || 0),
        priceTreatmentId: selectedAppointmentTreatmentId,
      }
    })
  }, [selectedAppointmentTreatmentId, treatments, setTreatmentOdontogramForm])

  const patientName = getPatientName(appointment, patientSnapshot)
  const treatmentName = getTreatmentName(appointment)
  const doctorName = getDoctorName(appointment)
  const chairName = getChairName(appointment)
  const startText = getDateText(appointment?.startTime || appointment?.start, formatDateTime)
  const currentVisualTag = getVisualTagColor(appointment)

  const financialMini = {
    produced: pickNumber(financialSummary, ["produced", "production", "totalProduced", "producedAmount"]),
    charged: pickNumber(financialSummary, ["charged", "collected", "paid", "totalPaid", "paidAmount", "chargedAmount"]),
    pending: pickNumber(financialSummary, ["pendingReceivables", "pending", "pendingAmount", "remaining", "balance", "accountsReceivable"]),
    credited: pickNumber(financialSummary, ["paidOnReceivables", "creditedToReceivable", "abonadoCxC", "credited"]),
    totalToCollect: pickNumber(financialSummary, ["totalReceivable", "totalToCollect", "totalPending", "receivableTotal"]),
  }



  const ledger = buildAppointmentLedger({ financialSummary, formatMoney, formatDateTime })
  const registeredTreatmentsCount = ledger.treatments.length
  const registeredPaymentsCount = ledger.payments.length
  const registeredReceivablesCount = ledger.receivables.length
  const hasRegisteredTreatment = registeredTreatmentsCount > 0
  const hasRegisteredPayment = registeredPaymentsCount > 0
  const hasRegisteredReceivable = registeredReceivablesCount > 0
  const hasPatientArrival = hasAppointmentArrivalSignal(appointment, statusRaw)
  const hasLinkedPlanMovement = hasAppointmentPlanLinkSignal(appointment)
  const hasFinancialFootprint =
    hasRegisteredPayment ||
    hasRegisteredReceivable ||
    financialMini.produced > 0 ||
    financialMini.charged > 0 ||
    financialMini.pending > 0 ||
    financialMini.credited > 0 ||
    financialMini.totalToCollect > 0
  const hasClinicalOrFinancialMovement =
    hasRegisteredTreatment ||
    hasFinancialFootprint
  const hasAnyProtectedAppointmentTrace =
    hasPatientArrival ||
    hasClinicalOrFinancialMovement ||
    hasLinkedPlanMovement

  const deleteAppointmentBlockedReason = getDeleteAppointmentBlockedReason({
    statusRaw,
    hasPatientArrival,
    hasRegisteredTreatment,
    hasFinancialFootprint,
    hasLinkedPlanMovement,
  })

  const cancelAppointmentBlockedReason = getCancelAppointmentBlockedReason({
    statusRaw,
    hasClinicalOrFinancialMovement,
  })

  const canCancelAppointment =
    !isReadOnly &&
    Boolean(appointmentId) &&
    !cancelAppointmentBlockedReason

  const canDeleteAppointment =
    !isReadOnly &&
    Boolean(appointmentId) &&
    !hasAnyProtectedAppointmentTrace &&
    !deleteAppointmentBlockedReason

  const appointmentFinancialStatus = getAppointmentFinancialStatus({
    appointment,
    financialMini,
    hasRegisteredTreatment,
    hasRegisteredPayment,
  })

  useEffect(() => {
    if (!appointmentId || loadingFinancialSummary) return
    if (autoTabAppointmentId === appointmentId) return

    setActiveTab(resolveDefaultOperationalTab({
      statusRaw,
      hasRegisteredTreatment,
      hasRegisteredPayment,
      pendingAmount: financialMini.pending,
      producedAmount: financialMini.produced,
      chargedAmount: financialMini.charged,
    }))
    setAutoTabAppointmentId(appointmentId)
  }, [
    appointmentId,
    loadingFinancialSummary,
    autoTabAppointmentId,
    statusRaw,
    hasRegisteredTreatment,
    hasRegisteredPayment,
    financialMini.pending,
    financialMini.produced,
    financialMini.charged,
  ])

  if (!appointment) return null

  const safeTreatmentOptions = Array.isArray(treatments) ? treatments : []
  const safeSurfaces = Array.isArray(surfaces) ? surfaces : []
  const safeConditions = Array.isArray(conditions) ? conditions : []
  const safeOdontoStatuses = Array.isArray(odontoStatuses) ? odontoStatuses : []

  const selectedClinicalTreatment = safeTreatmentOptions.find(
    (item) => String(item.id) === String(selectedAppointmentTreatmentId || "")
  )
  const selectedClinicalTreatmentPrice = getTreatmentCatalogPrice(selectedClinicalTreatment)
  const currentClinicalPriceRaw = treatmentOdontogramForm?.price ?? ""

  const amountToCollect = Math.max(
    0,
    financialMini.pending > 0
      ? financialMini.pending
      : financialMini.produced > financialMini.charged
        ? financialMini.produced - financialMini.charged
        : 0,
  )

  const currentPaymentAmount = parseMoneyInput(paymentForm?.amount || "")
  const hasValidPaymentAmount = currentPaymentAmount > 0

  const fillPaymentAmount = (value) => {
    const parsed = Number(value || 0)
    if (!Number.isFinite(parsed) || parsed <= 0) return
    setPaymentForm?.((prev) => ({ ...prev, amount: String(parsed) }))
  }

  const handleRegisterPaymentFromDrawer = async () => {
    if (registeringPayment || isReadOnly) return
    const ok = await onRegisterPayment?.()
    if (ok) {
      setPaymentForm?.((prev) => ({ ...prev, amount: "", notes: "" }))
    }
  }

  const openWorkspaceTab = (tab) => {
    if (typeof onOpenPatientWorkspace === "function") {
      onOpenPatientWorkspace(tab)
    }
  }

  const handleCheckInFromDrawer = async () => {
    const ok = await onCheckIn?.()
    if (ok) setActiveTab(TAB_CLINICAL)
  }

  const handleOpenPlanMiniView = () => {
    if (typeof onOpenTreatmentPlanModal === "function") {
      onOpenTreatmentPlanModal()
      return
    }

    openWorkspaceTab("plans")
  }

  const handleRegisterTreatmentAndContinue = async () => {
    const ok = await onRegisterTreatment?.()
    if (ok) {
      setSelectedAppointmentTreatmentId?.("")
      setActiveTab(TAB_BILLING)
    }
  }

  const handleAddAnotherTreatment = () => {
    setSelectedAppointmentTreatmentId?.("")
    setTreatmentOdontogramForm?.((prev) => ({
      ...prev,
      price: "",
      priceTreatmentId: "",
    }))
    setActiveTab(TAB_CLINICAL)
  }

  const nextStep = getNextStepConfig({
    status: statusRaw,
    checkingIn,
    registeringTreatment,
    registeringPayment,
    isReadOnly,
    onCheckIn: handleCheckInFromDrawer,
    onGoClinical: () => setActiveTab(TAB_CLINICAL),
    onGoBilling: () => setActiveTab(TAB_BILLING),
    onGoLedger: () => setActiveTab(TAB_LEDGER),
    onRegisterTreatment: handleRegisterTreatmentAndContinue,
    onRegisterPayment: handleRegisterPaymentFromDrawer,
    hasSelectedTreatment: Boolean(selectedAppointmentTreatmentId),
    hasRegisteredTreatment,
    hasRegisteredPayment,
    pendingAmount: financialMini.pending,
    producedAmount: financialMini.produced,
    chargedAmount: financialMini.charged,
    paymentAmount: currentPaymentAmount,
  })

  return (
    <div style={overlayStyle} onMouseDown={onClose}>
      <div style={drawerStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={headerTopRowStyle}>
            <div style={eyebrowStyle}>Detalle de cita</div>

            <div style={headerActionsStyle}>
              <button
                type="button"
                onClick={() => setEditModalOpen(true)}
                disabled={isReadOnly || !appointmentId}
                title={isReadOnly ? readOnlyReason || "Modo solo lectura activo" : "Editar cita"}
                style={headerEditButtonStyle}
              >
                Editar cita
              </button>
              <button
                onClick={onCancelAppointment}
                disabled={!canCancelAppointment}
                title={cancelAppointmentBlockedReason || "Cancelar cita"}
                style={getDestructiveHeaderButtonStyle(!canCancelAppointment, "soft")}
              >
                Cancelar
              </button>
              <button
                onClick={onDeleteAppointment}
                disabled={!canDeleteAppointment}
                title={deleteAppointmentBlockedReason || "Eliminar cita"}
                style={getDestructiveHeaderButtonStyle(!canDeleteAppointment, "hard")}
              >
                Eliminar
              </button>
              <button onClick={onClose} style={closeButtonStyle} aria-label="Cerrar detalle">×</button>
            </div>
          </div>

          <div style={headerMainStyle}>
            <div style={titleStyle} title={patientName}>{patientName}</div>
            <div style={subtitleStyle}>{treatmentName || "Sin tratamiento"} · {chairName || "Sin silla"} · {doctorName || "Sin doctor"}</div>
            <div style={metaRowStyle}>
              <HeaderPill>{statusDisplay}</HeaderPill>
              <HeaderPill>{startText || "Sin fecha"}</HeaderPill>
              <HeaderPill tone={appointmentFinancialStatus.tone}>{appointmentFinancialStatus.label}</HeaderPill>
            </div>
          </div>
        </div>

        {isReadOnly ? <div style={dangerCardStyle}>{readOnlyReason || "Modo solo lectura activo"}</div> : null}
        {!isReadOnly && deleteAppointmentBlockedReason ? (
          <div style={auditGuardCardStyle}>
            {deleteAppointmentBlockedReason}
          </div>
        ) : null}

        <div style={compactRibbonStyle}>
          <div style={visualTagRibbonStyle} aria-label="Marca visual de la cita">
            <button type="button" onClick={() => onUpdateAppointmentColor?.(null)} style={visualColorButtonStyle(null, !currentVisualTag, true)} title="Sin color">×</button>
            {VISUAL_TAG_COLORS.map((color) => (
              <button key={color} type="button" onClick={() => onUpdateAppointmentColor?.(color)} style={visualColorButtonStyle(color, currentVisualTag === color)} title={color} />
            ))}
          </div>
          <div style={quickActionsRowStyle}>
            <button onClick={() => onOpenPatientPage?.("SUMMARY")} style={quickActionChipStyle}>Ficha</button>
            <button onClick={() => onOpenPatientPage?.("ODONTOGRAM")} style={quickActionChipStyle}>Odontograma</button>
            <button onClick={handleOpenPlanMiniView} style={quickActionChipStyle}>Plan</button>
          </div>
        </div>

        <div style={nextStepSlimWrapperStyle}>
          <div style={nextStepSlimCardStyle}>
            <div style={nextStepSlimLabelStyle}>Siguiente:</div>
            <div style={nextStepSlimTitleStyle}>{nextStep.title}</div>
            {nextStep.primaryAction ? (
              <button onClick={nextStep.primaryAction.onClick} disabled={nextStep.primaryAction.disabled} style={getPrimaryNextStepButtonStyle(nextStep.tone, nextStep.primaryAction.disabled)}>
                {nextStep.primaryAction.label}
              </button>
            ) : null}
          </div>
        </div>

        <div style={tabsWrapperStyle}>
          <TabButton active={activeTab === TAB_OVERVIEW} onClick={() => setActiveTab(TAB_OVERVIEW)}>Resumen</TabButton>
          <TabButton active={activeTab === TAB_CLINICAL} onClick={() => setActiveTab(TAB_CLINICAL)}>Clínico</TabButton>
          <TabButton active={activeTab === TAB_BILLING} onClick={() => setActiveTab(TAB_BILLING)}>Cobro</TabButton>
          <TabButton active={activeTab === TAB_LEDGER} onClick={() => setActiveTab(TAB_LEDGER)}>Bitácora</TabButton>
        </div>

        <div style={bodyStyle}>
          {activeTab === TAB_OVERVIEW ? (
            <div style={contentGridStyle}>
              <Card title="Resumen financiero">
                {loadingFinancialSummary ? (
                  <MutedText>Cargando resumen financiero...</MutedText>
                ) : (
                  <div style={compactKpiRowStyle}>
                    <KpiMiniCard label="Producido" value={formatMoney?.(financialMini.produced) || "$0.00"} tone="neutral" compact />
                    <KpiMiniCard label="Cobrado" value={formatMoney?.(financialMini.charged) || "$0.00"} tone="neutral" compact />
                    <KpiMiniCard label="Pendiente" value={formatMoney?.(financialMini.pending) || "$0.00"} tone={financialMini.pending > 0 ? "warning" : "success"} compact />
                  </div>
                )}
              </Card>
              {appointment?.notes ? (
                <Card title="Notas de la cita">
                  <MutedText>{appointment.notes}</MutedText>
                </Card>
              ) : null}
            </div>
          ) : null}

          {activeTab === TAB_LEDGER ? (
            <div style={contentGridStyle}>
              <AppointmentLedgerCard
                ledger={ledger}
                loading={loadingFinancialSummary}
                formatMoney={formatMoney}
                onAddTreatment={handleAddAnotherTreatment}
                onDeleteTreatment={onDeleteTreatment}
                onCancelPayment={onCancelPayment}
                isReadOnly={isReadOnly}
                registeringTreatment={registeringTreatment}
                registeringPayment={registeringPayment}
              />
            </div>
          ) : null}

          {activeTab === TAB_CLINICAL ? (
            <div style={contentGridStyle}>
              <Card title="Registro clínico">
                <div style={formStackStyle}>
                  {hasRegisteredTreatment ? (
                    <InfoNotice title="Ya hay tratamiento registrado" text={`Esta cita ya tiene ${registeredTreatmentsCount} tratamiento${registeredTreatmentsCount === 1 ? "" : "s"} en la bitácora. Puedes agregar otro sin perder lo anterior.`} />
                  ) : null}

                  <div>
                    <FieldLabel>{hasRegisteredTreatment ? "Agregar otro tratamiento" : "Tratamiento"}</FieldLabel>
                    <select
                      value={selectedAppointmentTreatmentId || ""}
                      onChange={(e) => {
                        const nextTreatmentId = e.target.value
                        setSelectedAppointmentTreatmentId?.(nextTreatmentId)
                        const nextTreatment = safeTreatmentOptions.find((item) => String(item.id) === String(nextTreatmentId || ""))
                        const nextPrice = getTreatmentCatalogPrice(nextTreatment)
                        setTreatmentOdontogramForm?.((prev) => ({
                          ...prev,
                          price: nextTreatmentId ? String(nextPrice || 0) : "",
                          priceTreatmentId: nextTreatmentId || "",
                        }))
                      }}
                      style={inputStyle}
                    >
                      <option value="">Selecciona tratamiento</option>
                      {safeTreatmentOptions.map((t) => (
                        <option key={t.id} value={t.id}>{t.name} · {formatMoney?.(getTreatmentCatalogPrice(t)) || formatFallbackMoney(getTreatmentCatalogPrice(t))}</option>
                      ))}
                    </select>
                  </div>

                  <div style={twoColGridStyle}>
                    <div>
                      <FieldLabel>Precio</FieldLabel>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={currentClinicalPriceRaw}
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) =>
                          setTreatmentOdontogramForm?.((prev) => ({
                            ...prev,
                            price: normalizeMoneyInput(e.target.value),
                            priceTreatmentId: selectedAppointmentTreatmentId || prev?.priceTreatmentId || "",
                          }))
                        }
                        style={inputStyle}
                        placeholder="Ej. 2500"
                      />
                      <div style={helperTextStyle}>Lista: {formatMoney?.(selectedClinicalTreatmentPrice) || formatFallbackMoney(selectedClinicalTreatmentPrice)}</div>
                    </div>

                    <div>
                      <FieldLabel>Condición</FieldLabel>
                      <select
                        value={treatmentOdontogramForm?.condition || "Caries"}
                        onChange={(e) => setTreatmentOdontogramForm?.((prev) => ({ ...prev, condition: e.target.value }))}
                        style={inputStyle}
                      >
                        {safeConditions.map((condition) => (
                          <option key={condition} value={condition}>{condition}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={twoColGridStyle}>
                    <div>
                      <FieldLabel>Diente</FieldLabel>
                      <select
                        value={treatmentOdontogramForm?.tooth || ""}
                        onChange={(e) => setTreatmentOdontogramForm?.((prev) => ({
                          ...prev,
                          tooth: e.target.value ? Number(e.target.value) : null,
                        }))}
                        style={inputStyle}
                      >
                        <option value="">Sin pieza específica</option>
                        {FDI_TOOTH_GROUPS.map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.items.map((tooth) => (
                              <option key={tooth} value={tooth}>
                                {tooth}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div>
                      <FieldLabel>Superficie</FieldLabel>
                      <select
                        value={treatmentOdontogramForm?.surface || "ALL"}
                        onChange={(e) => setTreatmentOdontogramForm?.((prev) => ({ ...prev, surface: e.target.value }))}
                        style={inputStyle}
                      >
                        {safeSurfaces.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <details style={advancedDetailsStyle}>
                    <summary style={advancedSummaryStyle}>Opcional: estado clínico</summary>
                    <div>
                      <FieldLabel>Estado clínico</FieldLabel>
                      <select
                        value={treatmentOdontogramForm?.status || "ACTIVE"}
                        onChange={(e) => setTreatmentOdontogramForm?.((prev) => ({ ...prev, status: e.target.value }))}
                        style={inputStyle}
                      >
                        {safeOdontoStatuses.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </div>
                  </details>

                  <div style={clinicalActionsRowStyle}>
                    {hasRegisteredTreatment ? (
                      <button type="button" onClick={handleAddAnotherTreatment} disabled={registeringTreatment || isReadOnly} style={getInlineActionButtonStyle("neutral", registeringTreatment || isReadOnly)}>
                        Limpiar para otro tratamiento
                      </button>
                    ) : null}
                    <button
                      onClick={handleRegisterTreatmentAndContinue}
                      disabled={registeringTreatment || isReadOnly || !selectedAppointmentTreatmentId}
                      style={getActionButtonStyle("primary", registeringTreatment || isReadOnly || !selectedAppointmentTreatmentId)}
                    >
                      {registeringTreatment ? "Guardando..." : "Guardar tratamiento"}
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          ) : null}

          {activeTab === TAB_BILLING ? (
            <div style={contentGridStyle}>
              <Card title="Cobrar esta cita">
                {loadingFinancialSummary ? (
                  <MutedText>Cargando resumen financiero...</MutedText>
                ) : (
                  <div style={formStackStyle}>
                    <div style={compactKpiRowStyle}>
                      <KpiMiniCard label="Pendiente" value={formatMoney?.(financialMini.pending) || "$0.00"} compact />
                      <KpiMiniCard label="Cobrado" value={formatMoney?.(financialMini.charged) || "$0.00"} compact />
                      <KpiMiniCard label="Producido" value={formatMoney?.(financialMini.produced) || "$0.00"} compact />
                    </div>

                    {amountToCollect > 0 ? (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button type="button" onClick={() => fillPaymentAmount(amountToCollect)} disabled={registeringPayment || isReadOnly} style={getInlineActionButtonStyle("info", registeringPayment || isReadOnly)}>
                          Usar pendiente {formatMoney?.(amountToCollect) || formatFallbackMoney(amountToCollect)}
                        </button>
                      </div>
                    ) : null}

                    <div style={twoColGridStyle}>
                      <div>
                        <FieldLabel>Monto a cobrar</FieldLabel>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={paymentForm?.amount || ""}
                          onWheel={(e) => e.currentTarget.blur()}
                          onChange={(e) => setPaymentForm?.((prev) => ({ ...prev, amount: normalizeMoneyInput(e.target.value) }))}
                          style={inputStyle}
                          placeholder="Ej. 500"
                          disabled={registeringPayment || isReadOnly}
                        />
                      </div>
                      <div>
                        <FieldLabel>Método de pago</FieldLabel>
                        <select
                          value={paymentForm?.method || "CASH"}
                          onChange={(e) => setPaymentForm?.((prev) => ({ ...prev, method: e.target.value }))}
                          style={inputStyle}
                          disabled={registeringPayment || isReadOnly}
                        >
                          {paymentMethodOptions.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <details style={advancedDetailsStyle}>
                      <summary style={advancedSummaryStyle}>Agregar nota opcional</summary>
                      <textarea
                        value={paymentForm?.notes || ""}
                        onChange={(e) => setPaymentForm?.((prev) => ({ ...prev, notes: e.target.value }))}
                        style={{ ...inputStyle, minHeight: 86, resize: "vertical", paddingTop: 10 }}
                        placeholder="Ej. pago parcial, transferencia pendiente de confirmar"
                        disabled={registeringPayment || isReadOnly}
                      />
                    </details>

                    <button type="button" onClick={handleRegisterPaymentFromDrawer} disabled={registeringPayment || isReadOnly || !hasValidPaymentAmount} style={{ ...getActionButtonStyle("primary", registeringPayment || isReadOnly || !hasValidPaymentAmount), width: "100%", minHeight: 44 }}>
                      {registeringPayment ? "Guardando pago..." : "Guardar pago"}
                    </button>
                    {!hasValidPaymentAmount ? <MutedText>Ingresa un monto mayor a 0 para guardar el pago.</MutedText> : null}
                  </div>
                )}
              </Card>
            </div>
          ) : null}
        </div>

        {editModalOpen ? (
          <EditAppointmentModal
            open={editModalOpen}
            appointment={appointment}
            clinicId={appointment?.clinicId || appointment?.extendedProps?.clinicId || ""}
            treatments={treatments}
            doctors={doctors}
            chairs={chairs}
            saving={updatingAppointment}
            hasProtectedMovement={hasAnyProtectedAppointmentTrace}
            onClose={() => setEditModalOpen(false)}
            onSave={onUpdateAppointment}
          />
        ) : null}
      </div>
    </div>
  )
}

function AppointmentLedgerCard({
  ledger,
  loading,
  formatMoney,
  onAddTreatment,
  onDeleteTreatment,
  onCancelPayment,
  isReadOnly,
  registeringTreatment,
  registeringPayment,
}) {
  const hasItems = ledger.items.length > 0

  const handleDeleteTreatment = async (item) => {
    if (!item?.treatmentLogId) return
    const ok = window.confirm(`¿Eliminar el tratamiento ${item.title}? Solo se permite si no tiene pagos aplicados.`)
    if (!ok) return
    await onDeleteTreatment?.(item.treatmentLogId)
  }

  const handleCancelPayment = async (item) => {
    if (!item?.paymentId) return
    const ok = window.confirm(`¿Cancelar este pago por ${formatMoney?.(item.amount || 0) || formatFallbackMoney(item.amount || 0)}?`)
    if (!ok) return
    await onCancelPayment?.(item.paymentId)
  }

  return (
    <Card title="Bitácora de esta cita">
      {loading ? (
        <MutedText>Cargando bitácora...</MutedText>
      ) : hasItems ? (
        <div style={ledgerStackStyle}>
          {ledger.items.map((item) => (
            <LedgerItem
              key={item.key}
              item={item}
              formatMoney={formatMoney}
              onDeleteTreatment={handleDeleteTreatment}
              onCancelPayment={handleCancelPayment}
              isReadOnly={isReadOnly}
              registeringTreatment={registeringTreatment}
              registeringPayment={registeringPayment}
            />
          ))}

          <div style={ledgerFooterStyle}>
            <button type="button" onClick={onAddTreatment} style={getInlineActionButtonStyle("neutral", false)}>
              Agregar otro tratamiento
            </button>
          </div>
        </div>
      ) : (
        <div style={emptyLedgerStyle}>
          <div style={{ fontWeight: 950, color: "#0f172a" }}>Aún no hay movimientos registrados.</div>
          <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.45 }}>
            Cuando guardes un tratamiento o un pago, aparecerá aquí sin recargar.
          </div>
        </div>
      )}
    </Card>
  )
}

function LedgerItem({
  item,
  formatMoney,
  onDeleteTreatment,
  onCancelPayment,
  isReadOnly,
  registeringTreatment,
  registeringPayment,
}) {
  const toneStyle = getPillToneStyle(item.tone || "neutral")
  const isTreatment = item.kind === "treatment"
  const isPayment = item.kind === "payment"
  const deleteDisabled = isReadOnly || registeringTreatment || item.locked
  const paymentDisabled = isReadOnly || registeringPayment

  return (
    <div style={ledgerItemStyle}>
      <div style={ledgerItemHeaderStyle}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={ledgerTypeStyle}>{item.typeLabel}</div>
          <div style={ledgerTitleStyle}>{item.title}</div>
          {item.description ? <div style={ledgerDescriptionStyle}>{item.description}</div> : null}
        </div>
        <div style={ledgerAmountBlockStyle}>
          <div style={ledgerAmountStyle}>{formatMoney?.(item.amount || 0) || formatFallbackMoney(item.amount || 0)}</div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              marginTop: 4,
              padding: "4px 8px",
              borderRadius: 999,
              background: toneStyle.background,
              border: toneStyle.border,
              color: toneStyle.color,
              fontSize: 11,
              fontWeight: 900,
            }}
          >
            {item.statusLabel}
          </div>
        </div>
      </div>

      <div style={ledgerBottomRowStyle}>
        {item.meta?.length ? (
          <div style={ledgerMetaRowStyle}>
            {item.meta.map((meta) => (
              <span key={meta}>{meta}</span>
            ))}
          </div>
        ) : <span />}

        <div style={ledgerActionsStyle}>
          {isTreatment ? (
            <button
              type="button"
              onClick={() => onDeleteTreatment?.(item)}
              disabled={deleteDisabled}
              title={item.locked ? "Primero cancela el pago relacionado" : "Eliminar tratamiento"}
              style={getMiniDangerButtonStyle(deleteDisabled)}
            >
              Eliminar
            </button>
          ) : null}

          {isPayment ? (
            <button
              type="button"
              onClick={() => onCancelPayment?.(item)}
              disabled={paymentDisabled}
              style={getMiniDangerButtonStyle(paymentDisabled)}
            >
              Cancelar
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function buildAppointmentLedger({ financialSummary, formatMoney, formatDateTime }) {
  const treatments = Array.isArray(financialSummary?.treatments)
    ? financialSummary.treatments
    : []
  const payments = Array.isArray(financialSummary?.payments)
    ? financialSummary.payments
    : []
  const receivables = Array.isArray(financialSummary?.receivables)
    ? financialSummary.receivables
    : Array.isArray(financialSummary?.accountsReceivable)
      ? financialSummary.accountsReceivable
      : []

  const treatmentItems = treatments.map((item, index) => {
    const price = pickFirstNumber(item, ["price", "amount", "totalAmount"])
    const createdAt = formatLedgerDate(item.createdAt, formatDateTime)
    const linkedReceivable = receivables.find((r) => {
      const treatmentLogId = r?.treatmentLogId || r?.treatmentLog?.id || ""
      return treatmentLogId && String(treatmentLogId) === String(item.treatmentLogId || item.id || "")
    })
    const balance = linkedReceivable ? pickFirstNumber(linkedReceivable, ["balance", "pending", "pendingAmount"]) : 0
    const paidAmount = linkedReceivable ? pickFirstNumber(linkedReceivable, ["paidAmount", "paid", "charged"]) : 0

    return {
      key: `treatment-${item.id || item.treatmentLogId || index}`,
      kind: "treatment",
      treatmentLogId: item.treatmentLogId || item.id || "",
      locked: paidAmount > 0,
      typeLabel: "Tratamiento",
      title: item.treatmentName || item.name || "Tratamiento registrado",
      description: balance > 0
        ? `Pendiente: ${formatMoney?.(balance) || formatFallbackMoney(balance)}`
        : paidAmount > 0
          ? `Pagado: ${formatMoney?.(paidAmount) || formatFallbackMoney(paidAmount)}`
          : "Registrado para esta atención",
      amount: price,
      statusLabel: balance > 0 ? "Pendiente" : paidAmount > 0 ? "Pagado" : "Registrado",
      tone: balance > 0 ? "warning" : paidAmount > 0 ? "success" : "info",
      createdAt: item.createdAt || null,
      meta: [createdAt].filter(Boolean),
    }
  })

  const paymentItems = payments.map((item, index) => {
    const amount = pickFirstNumber(item, ["amount", "paidAmount"])
    const unallocated = pickFirstNumber(item, ["unallocatedAmount", "unassigned"])
    const allocated = Math.max(0, amount - unallocated)
    const method = translatePaymentMethod(item.method)
    const createdAt = formatLedgerDate(item.createdAt, formatDateTime)

    return {
      key: `payment-${item.id || index}`,
      kind: "payment",
      paymentId: item.id || "",
      typeLabel: "Pago",
      title: method,
      description: allocated > 0
        ? `Aplicado a CxC: ${formatMoney?.(allocated) || formatFallbackMoney(allocated)}`
        : unallocated > 0
          ? `Saldo a favor/sin asignar: ${formatMoney?.(unallocated) || formatFallbackMoney(unallocated)}`
          : item.notes || "Pago registrado",
      amount,
      statusLabel: item.status ? translateLedgerStatus(item.status) : "Registrado",
      tone: "success",
      createdAt: item.createdAt || null,
      meta: [createdAt, item.notes].filter(Boolean),
    }
  })

  const receivableItems = receivables.map((item, index) => {
    const total = pickFirstNumber(item, ["totalAmount", "amount"])
    const balance = pickFirstNumber(item, ["balance", "pending", "pendingAmount"])
    const paid = pickFirstNumber(item, ["paidAmount", "paid"])
    const createdAt = formatLedgerDate(item.createdAt, formatDateTime)

    return {
      key: `receivable-${item.id || index}`,
      kind: "receivable",
      typeLabel: "CxC",
      title: item.treatmentName || item.concept || "Cuenta por cobrar",
      description: `Pagado: ${formatMoney?.(paid) || formatFallbackMoney(paid)} · Saldo: ${formatMoney?.(balance) || formatFallbackMoney(balance)}`,
      amount: total,
      statusLabel: balance > 0 ? "Pendiente" : "Liquidada",
      tone: balance > 0 ? "warning" : "success",
      createdAt: item.createdAt || null,
      meta: [createdAt].filter(Boolean),
    }
  })

  const items = [...treatmentItems, ...paymentItems]
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())

  return {
    treatments: treatmentItems,
    payments: paymentItems,
    receivables: receivableItems,
    items,
  }
}

function hasAppointmentArrivalSignal(appointment, statusRaw) {
  const raw = String(statusRaw || "").toUpperCase()
  if (["CHECKED_IN", "ARRIVED", "IN_PROGRESS", "COMPLETED", "FINISHED"].includes(raw)) {
    return true
  }

  return Boolean(
    appointment?.checkedIn ||
      appointment?.checkedInTime ||
      appointment?.checkedInAt ||
      appointment?.arrivalTime ||
      appointment?.arrivedAt ||
      appointment?.extendedProps?.checkedIn ||
      appointment?.extendedProps?.checkedInTime ||
      appointment?.extendedProps?.checkedInAt ||
      appointment?.extendedProps?.arrivalTime ||
      appointment?.extendedProps?.arrivedAt
  )
}

function hasAppointmentPlanLinkSignal(appointment) {
  const sources = [
    appointment?.treatmentPlanItemId,
    appointment?.treatmentPlanId,
    appointment?.planId,
    appointment?.treatmentPlanItem?.id,
    appointment?.treatmentPlan?.id,
    appointment?.extendedProps?.treatmentPlanItemId,
    appointment?.extendedProps?.treatmentPlanId,
    appointment?.extendedProps?.planId,
  ]

  if (sources.some(Boolean)) return true

  if (Array.isArray(appointment?.treatmentPlanItems) && appointment.treatmentPlanItems.length) {
    return true
  }

  if (Array.isArray(appointment?.extendedProps?.treatmentPlanItems) && appointment.extendedProps.treatmentPlanItems.length) {
    return true
  }

  return false
}

function getDeleteAppointmentBlockedReason({
  statusRaw,
  hasPatientArrival,
  hasRegisteredTreatment,
  hasFinancialFootprint,
  hasLinkedPlanMovement,
}) {
  const raw = String(statusRaw || "").toUpperCase()

  if (["COMPLETED", "FINISHED"].includes(raw)) {
    return "Esta cita ya fue cerrada. No se puede eliminar; corrige desde la bitácora para conservar historial."
  }

  if (hasRegisteredTreatment || hasFinancialFootprint) {
    return "Esta cita ya tiene movimientos clínicos o financieros ligados. No se puede eliminar; corrige pagos o tratamientos desde Bitácora."
  }

  if (hasPatientArrival) {
    return "Esta cita ya registró llegada del paciente. No se puede eliminar; puedes cancelarla si aún no tiene movimientos clínicos o financieros."
  }

  if (hasLinkedPlanMovement) {
    return "Esta cita está ligada a un plan de tratamiento. No se elimina para evitar inconsistencias; cancélala o reprograma el ítem."
  }

  return ""
}

function getCancelAppointmentBlockedReason({ statusRaw, hasClinicalOrFinancialMovement }) {
  const raw = String(statusRaw || "").toUpperCase()

  if (raw === "CANCELLED") return "La cita ya está cancelada"

  if (hasClinicalOrFinancialMovement) {
    return "Esta cita todavía tiene movimientos clínicos o financieros activos. Cancela pagos o elimina tratamientos desde Bitácora antes de cancelar la cita."
  }

  return ""
}

function getAppointmentFinancialStatus({ appointment, financialMini, hasRegisteredTreatment, hasRegisteredPayment }) {
  const produced = Number(financialMini?.produced || 0)
  const charged = Number(financialMini?.charged || 0)
  const pending = Number(financialMini?.pending || 0)

  if (pending > 0 && charged > 0) return { label: "Pago parcial", tone: "warning" }
  if (pending > 0) return { label: "Pago pendiente", tone: "warning" }
  if (produced > 0 && charged <= 0) return { label: "Pago pendiente", tone: "warning" }
  if ((hasRegisteredPayment || charged > 0) && pending <= 0) return { label: "Pagada", tone: "success" }
  if (hasRegisteredTreatment) return { label: "Tratamiento registrado", tone: "info" }

  const raw = String(
    appointment?.financialStatus ||
    appointment?.extendedProps?.financialStatus ||
    ""
  ).toUpperCase()

  if (raw === "PAID") return { label: "Pagada", tone: "success" }
  if (raw === "PARTIAL") return { label: "Pago parcial", tone: "warning" }
  if (raw === "DEBT") return { label: "Pago pendiente", tone: "warning" }

  return { label: "Sin cobro", tone: "neutral" }
}

function pickFirstNumber(source, keys) {
  for (const key of keys) {
    const value = source?.[key]
    if (typeof value === "number") return value
    if (typeof value === "string" && value !== "" && !Number.isNaN(Number(value))) {
      return Number(value)
    }
  }
  return 0
}

function formatLedgerDate(value, formatDateTime) {
  if (!value) return ""
  try {
    if (typeof formatDateTime === "function") return formatDateTime(value)
    return new Date(value).toLocaleString("es-MX")
  } catch {
    return ""
  }
}

function translatePaymentMethod(method) {
  const raw = String(method || "").toUpperCase()
  const found = paymentMethodOptions.find((item) => item.value === raw)
  return found?.label || method || "Pago"
}

function translateLedgerStatus(status) {
  const raw = String(status || "").toUpperCase()
  if (raw === "POSTED") return "Registrado"
  if (raw === "CANCELLED") return "Cancelado"
  if (raw === "PENDING") return "Pendiente"
  if (raw === "PAID") return "Pagado"
  return status || "Registrado"
}

function getPillToneStyle(tone = "neutral") {
  const tones = {
    neutral: {
      background: "#f8fafc",
      border: "1px solid #cbd5e1",
      color: "#334155",
      dot: "#64748b",
    },
    info: {
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      color: "#1d4ed8",
      dot: "#2563eb",
    },
    success: {
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
      color: "#166534",
      dot: "#16a34a",
    },
    warning: {
      background: "#fff7ed",
      border: "1px solid #fed7aa",
      color: "#c2410c",
      dot: "#ea580c",
    },
    danger: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#991b1b",
      dot: "#dc2626",
    },
  }

  return tones[tone] || tones.neutral
}

function getKpiToneStyle(tone = "neutral") {
  const tones = {
    neutral: {
      border: "1px solid #e2e8f0",
      background: "#f8fafc",
    },
    success: {
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
    },
    warning: {
      border: "1px solid #fed7aa",
      background: "#fff7ed",
    },
  }

  return tones[tone] || tones.neutral
}


function normalizeMoneyInput(value) {
  const raw = String(value || "").replace(/,/g, ".")
  let result = ""
  let dotUsed = false

  for (const char of raw) {
    if (/\d/.test(char)) {
      result += char
      continue
    }

    if (char === "." && !dotUsed) {
      result += char
      dotUsed = true
    }
  }

  return result
}

function parseMoneyInput(value) {
  const parsed = Number(normalizeMoneyInput(value))
  return Number.isFinite(parsed) ? parsed : 0
}

function getTreatmentCatalogPrice(treatment) {
  if (!treatment) return 0

  const candidate =
    treatment.price ??
    treatment.amount ??
    treatment.cost ??
    treatment.defaultPrice ??
    treatment.suggestedPrice ??
    0

  const parsed = Number(candidate || 0)
  return Number.isFinite(parsed) ? parsed : 0
}

function formatFallbackMoney(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safe)
}

function getDestructiveHeaderButtonStyle(disabled, mode = "soft") {
  const visual =
    mode === "hard"
      ? {
          border: "1px solid #fecaca",
          background: "#fef2f2",
          color: "#991b1b",
        }
      : {
          border: "1px solid #fed7aa",
          background: "#fff7ed",
          color: "#c2410c",
        }

  return {
    borderRadius: 12,
    padding: "7px 9px",
    fontSize: 12,
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    ...visual,
  }
}

function getStatusRaw(appointment) {
  const raw = String(
    appointment?.status ||
    appointment?.extendedProps?.status ||
    ""
  ).toUpperCase()

  const checkedIn =
    Boolean(appointment?.checkedIn) ||
    Boolean(appointment?.extendedProps?.checkedIn) ||
    Boolean(appointment?.checkedInTime) ||
    Boolean(appointment?.extendedProps?.checkedInTime)

  if (checkedIn && !["COMPLETED", "FINISHED", "CANCELLED"].includes(raw)) {
    return "CHECKED_IN"
  }

  return raw
}

function getStatusDisplayLabel(appointment, statusLabel) {
  const raw = getStatusRaw(appointment)

  if (raw === "CHECKED_IN") return "En atención"

  return statusLabel?.(appointment?.status) || raw || "—"
}

function getDefaultTabByStatus(status) {
  if (status === "CHECKED_IN" || status === "IN_PROGRESS") return TAB_CLINICAL
  if (status === "COMPLETED" || status === "FINISHED") return TAB_BILLING
  return TAB_OVERVIEW
}

function resolveDefaultOperationalTab({
  statusRaw,
  hasRegisteredTreatment,
  hasRegisteredPayment,
  pendingAmount,
  producedAmount,
  chargedAmount,
}) {
  const status = String(statusRaw || "").toUpperCase()
  const pending = Number(pendingAmount || 0)
  const produced = Number(producedAmount || 0)
  const charged = Number(chargedAmount || 0)

  if (["SCHEDULED", "CONFIRMED", "PROGRAMMED", "BOOKED"].includes(status)) {
    return TAB_OVERVIEW
  }

  if (!hasRegisteredTreatment && produced <= 0) {
    return ["CHECKED_IN", "IN_PROGRESS", "COMPLETED", "FINISHED"].includes(status)
      ? TAB_CLINICAL
      : TAB_OVERVIEW
  }

  if (hasRegisteredTreatment && (pending > 0 || charged <= 0 || !hasRegisteredPayment)) {
    return TAB_BILLING
  }

  if (hasRegisteredTreatment && pending <= 0 && (hasRegisteredPayment || charged > 0)) {
    return TAB_BILLING
  }

  return getDefaultTabByStatus(status)
}

function getNextStepConfig({
  status,
  checkingIn,
  registeringTreatment,
  registeringPayment,
  isReadOnly,
  onCheckIn,
  onGoClinical,
  onGoBilling,
  onGoLedger,
  onRegisterTreatment,
  onRegisterPayment,
  hasSelectedTreatment,
  hasRegisteredTreatment,
  hasRegisteredPayment,
  pendingAmount,
  producedAmount,
  chargedAmount,
  paymentAmount,
}) {
  const pending = Number(pendingAmount || 0)
  const produced = Number(producedAmount || 0)
  const charged = Number(chargedAmount || 0)
  const hasDebt = pending > 0 || (hasRegisteredTreatment && produced > charged)
  const normalizedStatus = String(status || "").toUpperCase()

  if (normalizedStatus === "CANCELLED") {
    return {
      tone: "warning",
      title: "Cita cancelada",
      primaryAction: null,
    }
  }

  if (!hasRegisteredTreatment && ["SCHEDULED", "CONFIRMED", "PROGRAMMED", "BOOKED"].includes(normalizedStatus)) {
    return {
      tone: "success",
      title: "Confirma la llegada",
      primaryAction: {
        label: checkingIn ? "Confirmando..." : "Confirmar llegada",
        onClick: onCheckIn,
        disabled: checkingIn || isReadOnly,
      },
    }
  }

  if (!hasRegisteredTreatment) {
    return {
      tone: "info",
      title: "Registra lo realizado",
      primaryAction: {
        label: "Ir a Clínico",
        onClick: onGoClinical,
        disabled: false,
      },
      secondaryAction: hasSelectedTreatment
        ? {
            label: registeringTreatment ? "Guardando..." : "Guardar tratamiento",
            onClick: onRegisterTreatment,
            disabled: registeringTreatment || isReadOnly,
          }
        : null,
    }
  }

  if (hasDebt || !hasRegisteredPayment) {
    return {
      tone: "warning",
      title: charged > 0 ? "Continúa con el cobro" : "Registra cobro",
      primaryAction: {
        label: "Ir a Cobro",
        onClick: onGoBilling,
        disabled: false,
      },
      secondaryAction:
        paymentAmount > 0
          ? {
              label: registeringPayment ? "Guardando..." : "Guardar pago",
              onClick: onRegisterPayment,
              disabled: registeringPayment || isReadOnly,
            }
          : null,
    }
  }

  return {
    tone: "success",
    title: "Cita liquidada",
    primaryAction: {
      label: "Ver bitácora",
      onClick: onGoLedger,
      disabled: false,
    },
  }
}

function getPatientName(appointment, patientSnapshot) {
  const snapshotName = `${patientSnapshot?.patient?.firstName || ""} ${patientSnapshot?.patient?.lastName || ""}`.trim()
  return snapshotName || appointment?.patientName || appointment?.title || "Paciente"
}

function getTreatmentName(appointment) {
  return (
    appointment?.treatmentName ||
    appointment?.reason ||
    appointment?.extendedProps?.treatmentName ||
    "Sin tratamiento"
  )
}

function getDoctorName(appointment) {
  return (
    appointment?.doctorName ||
    appointment?.extendedProps?.doctorName ||
    appointment?.doctor?.name ||
    "Sin doctor"
  )
}

function getChairName(appointment) {
  return (
    appointment?.chairName ||
    appointment?.extendedProps?.chairName ||
    appointment?.chair?.name ||
    "Sin silla"
  )
}

function getVisualTagColor(appointment) {
  return (
    appointment?.customTagColor ||
    appointment?.extendedProps?.customTagColor ||
    appointment?.extendedProps?.markerColor ||
    null
  )
}

function getDateText(value, formatDateTime) {
  if (!value) return ""
  try {
    if (typeof formatDateTime === "function") return formatDateTime(value)
    return new Date(value).toLocaleString("es-MX")
  } catch {
    return ""
  }
}

function pickNumber(source, keys) {
  if (!source) return 0
  for (const key of keys) {
    const value = source?.[key]
    if (typeof value === "number") return value
    if (typeof value === "string" && value !== "" && !Number.isNaN(Number(value))) {
      return Number(value)
    }
  }
  return 0
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: active ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
        background: active ? "#eff6ff" : "#ffffff",
        color: active ? "#1d4ed8" : "#334155",
        borderRadius: 11,
        padding: "8px 6px",
        fontWeight: 900,
        fontSize: 12,
        lineHeight: 1,
        cursor: "pointer",
        minWidth: 0,
      }}
    >
      {children}
    </button>
  )
}

function Card({ title, subtitle, children }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        padding: 14,
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>{title}</div>
        {subtitle ? (
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>{subtitle}</div>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function KpiMiniCard({ label, value, tone = "neutral", compact = false }) {
  const toneStyle = getKpiToneStyle(tone)

  return (
    <div
      style={{
        border: toneStyle.border,
        background: toneStyle.background,
        borderRadius: 14,
        padding: compact ? "8px 7px" : 12,
      }}
    >
      <div style={{ fontSize: compact ? 10 : 11, color: "#64748b", fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: compact ? 12.5 : 20, fontWeight: 900, color: "#0f172a", marginTop: 4, whiteSpace: "nowrap" }}>
        {value}
      </div>
    </div>
  )
}

function HeaderPill({ children, tone = "neutral" }) {
  const toneStyle = getPillToneStyle(tone)

  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        background: toneStyle.background,
        border: toneStyle.border,
        color: toneStyle.color,
        fontSize: 12,
        fontWeight: 850,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </div>
  )
}

function FieldLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: "#64748b",
        fontWeight: 800,
        marginBottom: 0,
      }}
    >
      {children}
    </div>
  )
}

function MutedText({ children }) {
  return <div style={{ color: "#64748b", fontSize: 13 }}>{children}</div>
}

function InfoNotice({ title, text }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "#eff6ff",
        border: "1px solid #bfdbfe",
        color: "#1e3a8a",
        fontSize: 12.5,
        lineHeight: 1.45,
        fontWeight: 750,
      }}
    >
      <div style={{ fontWeight: 950, color: "#1d4ed8", marginBottom: 3 }}>
        {title}
      </div>
      <div>{text}</div>
    </div>
  )
}


function getActionButtonStyle(tone, disabled) {
  const tones = {
    primary: {
      background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
      color: "#ffffff",
      border: "none",
    },
    success: {
      background: "#dcfce7",
      color: "#166534",
      border: "1px solid #bbf7d0",
    },
    info: {
      background: "#dbeafe",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    },
    warning: {
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fed7aa",
    },
  }

  return {
    borderRadius: 12,
    padding: "8px 12px",
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
    ...tones[tone],
  }
}

function getInlineActionButtonStyle(tone = "neutral", disabled = false) {
  const tones = {
    neutral: {
      background: "#ffffff",
      color: "#334155",
      border: "1px solid #cbd5e1",
    },
    info: {
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    },
  }

  return {
    minHeight: 36,
    borderRadius: 12,
    padding: "8px 12px",
    fontSize: 12.5,
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
    ...tones[tone],
  }
}

function getPrimaryNextStepButtonStyle(tone, disabled) {
  const tones = {
    success: {
      background: "#16a34a",
      color: "#ffffff",
      border: "none",
    },
    info: {
      background: "#2563eb",
      color: "#ffffff",
      border: "none",
    },
    warning: {
      background: "#ea580c",
      color: "#ffffff",
      border: "none",
    },
    neutral: {
      background: "#334155",
      color: "#ffffff",
      border: "none",
    },
  }

  return {
    borderRadius: 12,
    padding: "10px 12px",
    fontSize: 12.5,
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
    ...tones[tone || "neutral"],
  }
}

function getMiniDangerButtonStyle(disabled) {
  return {
    minHeight: 28,
    borderRadius: 999,
    padding: "0 9px",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    fontSize: 11,
    fontWeight: 900,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    whiteSpace: "nowrap",
  }
}

function visualColorButtonStyle(color, active, isClear = false) {
  return {
    width: 22,
    height: 22,
    minWidth: 22,
    borderRadius: "50%",
    border: active ? "2px solid #0f172a" : "1px solid #cbd5e1",
    background: color || "#ffffff",
    color: isClear ? "#334155" : "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1,
    cursor: "pointer",
    padding: 0,
    boxShadow: active ? "0 0 0 2px rgba(37,99,235,0.12)" : "none",
  }
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 999997,
  background: "rgba(15, 23, 42, 0.18)",
  backdropFilter: "blur(2px)",
  display: "flex",
  justifyContent: "flex-end",
}

const drawerStyle = {
  width: "min(860px, 100vw)",
  height: "100vh",
  background: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  boxShadow: "-12px 0 40px rgba(15, 23, 42, 0.18)",
}

const headerStyle = {
  padding: "8px 12px 8px",
  borderBottom: "1px solid #e2e8f0",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  display: "grid",
  gap: 5,
}

const headerTopRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
}

const headerMainStyle = {
  minWidth: 0,
}

const headerActionsStyle = {
  display: "flex",
  gap: 5,
  flexWrap: "nowrap",
  justifyContent: "flex-end",
  alignItems: "center",
  flexShrink: 0,
}

const eyebrowStyle = {
  display: "inline-flex",
  padding: "3px 7px",
  borderRadius: 999,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1d4ed8",
  fontSize: 9,
  fontWeight: 900,
  letterSpacing: 0.45,
  textTransform: "uppercase",
  marginBottom: 6,
}

const titleStyle = {
  fontSize: "clamp(15px, 4.6vw, 18px)",
  fontWeight: 950,
  color: "#0f172a",
  lineHeight: 1.05,
  letterSpacing: "-0.03em",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
}

const subtitleStyle = {
  fontSize: 12.5,
  color: "#475569",
  marginTop: 4,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
}

const metaRowStyle = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  marginTop: 6,
}

const closeButtonStyle = {
  width: 34,
  height: 34,
  borderRadius: 11,
  fontSize: 21,
  lineHeight: 1,
  fontWeight: 900,
  cursor: "pointer",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#334155",
}

const dangerCardStyle = {
  margin: "14px 18px 0",
  padding: 12,
  borderRadius: 14,
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#991b1b",
  fontWeight: 800,
}

const auditGuardCardStyle = {
  margin: "10px 18px 0",
  padding: 12,
  borderRadius: 14,
  background: "#fffbeb",
  border: "1px solid #fde68a",
  color: "#92400e",
  fontWeight: 800,
  lineHeight: 1.35,
}

const compactRibbonStyle = {
  margin: "8px 12px 0",
  padding: "7px 8px",
  borderRadius: 16,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 8px 18px rgba(15, 23, 42, 0.045)",
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 7,
  justifyItems: "center",
}

const visualTagRibbonStyle = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  flexWrap: "nowrap",
  overflowX: "visible",
  minWidth: 0,
}

const quickActionsRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  flexWrap: "wrap",
  minWidth: 0,
}

const quickActionChipStyle = {
  minHeight: 28,
  borderRadius: 999,
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  color: "#334155",
  padding: "0 9px",
  fontSize: 11.5,
  fontWeight: 900,
  cursor: "pointer",
  whiteSpace: "nowrap",
}



const clinicalActionsRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  marginTop: 6,
}

const ledgerStackStyle = {
  display: "grid",
  gap: 10,
}

const ledgerItemStyle = {
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  borderRadius: 14,
  padding: 12,
}

const ledgerItemHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 12,
}

const ledgerAmountBlockStyle = {
  textAlign: "right",
  flexShrink: 0,
}

const ledgerBottomRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  marginTop: 8,
}

const ledgerActionsStyle = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  flexShrink: 0,
}

const ledgerTypeStyle = {
  fontSize: 10.5,
  fontWeight: 950,
  color: "#2563eb",
  textTransform: "uppercase",
  letterSpacing: 0.35,
}

const ledgerTitleStyle = {
  fontSize: 14,
  fontWeight: 950,
  color: "#0f172a",
  marginTop: 2,
}

const ledgerDescriptionStyle = {
  fontSize: 12.5,
  color: "#475569",
  marginTop: 3,
  lineHeight: 1.35,
}

const ledgerAmountStyle = {
  fontSize: 15,
  fontWeight: 950,
  color: "#0f172a",
  whiteSpace: "nowrap",
}

const ledgerMetaRowStyle = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
  marginTop: 6,
  color: "#64748b",
  fontSize: 11.5,
  fontWeight: 750,
}

const ledgerFooterStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 2,
}

const emptyLedgerStyle = {
  display: "grid",
  gap: 4,
  border: "1px dashed #cbd5e1",
  background: "#f8fafc",
  borderRadius: 14,
  padding: 12,
}

const nextStepSlimWrapperStyle = {
  margin: "8px 12px 0",
}

const nextStepSlimCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  borderRadius: 14,
  border: "1px solid #fed7aa",
  background: "#fff7ed",
  padding: "7px 9px",
}

const nextStepSlimLabelStyle = {
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  color: "#475569",
  whiteSpace: "nowrap",
}

const nextStepSlimTitleStyle = {
  fontSize: 13,
  fontWeight: 900,
  color: "#0f172a",
  flex: 1,
  minWidth: 0,
}

const headerEditButtonStyle = {
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
  borderRadius: 12,
  padding: "8px 12px",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer",
}

const tabsWrapperStyle = {
  margin: "8px 12px 0",
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 6,
}

const bodyStyle = {
  padding: "8px 12px 14px",
  overflow: "auto",
}

const contentGridStyle = {
  display: "grid",
  gap: 14,
}


const compactKpiRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 6,
}

const formStackStyle = {
  display: "grid",
  gap: 10,
}

const twoColGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 10,
}

const inputStyle = {
  width: "100%",
  minHeight: 42,
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  padding: "0 12px",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
}


const advancedDetailsStyle = {
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  borderRadius: 14,
  padding: 12,
  display: "grid",
  gap: 10,
}

const advancedSummaryStyle = {
  color: "#334155",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer",
  userSelect: "none",
}

const helperTextStyle = {
  marginTop: 6,
  color: "#64748b",
  fontSize: 12,
  lineHeight: 1.35,
}
